// src/Components/HPC_Report/services/preparatoryReportService.js
// Service for preparatory stage (Grades 3-5) HPC reports
import { API_CONFIG } from '../../../config/api';

const API_BASE_URL = API_CONFIG.BASE_URL;

/**
 * Fetches the user's educational stage based on their grade
 * @returns {Promise<Object>} Object containing grade and educational stage
 */
export const getUserStage = async () => {
    try {
        const accessToken = localStorage.getItem("accessToken");
        if (!accessToken) {
            throw new Error("Authentication required. Please log in again.");
        }

        const response = await fetch(`${API_BASE_URL}/user/stage`, {
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            // If the /user/stage endpoint is not available, determine the stage based on grade in user profile
            console.warn("Stage API not available, fetching user profile instead");
            return await determineStageFromUserProfile();
        }

        return await response.json();
    } catch (error) {
        console.error("Error fetching user stage:", error);
        // Fallback: If we can't determine the stage, return foundational as default
        return { grade: "Unknown", stage: "Foundational Stage" };
    }
};

/**
 * Helper function to determine stage from user profile if stage API is not available
 * @private
 */
const determineStageFromUserProfile = async () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
        throw new Error("Authentication required. Please log in again.");
    }

    const response = await fetch(`${API_BASE_URL}/users/me`, {
        headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
        }
    });

    if (!response.ok) {
        throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
    }

    const userData = await response.json();
    const grade = userData.grade || "";

    // Extract the first number found in the grade string
    const gradeMatch = grade.match(/\d+/);
    if (!gradeMatch) {
        return { grade, stage: "Foundational Stage" }; // Default to foundational if no number found
    }

    const gradeNum = parseInt(gradeMatch[0], 10);
    let stage = "Foundational Stage";

    if (gradeNum >= 3 && gradeNum <= 5) {
        stage = "Preparatory Stage";
    } else if (gradeNum >= 6 && gradeNum <= 8) {
        stage = "Middle Stage";
    } else if (gradeNum >= 9) {
        stage = "Secondary Stage";
    }

    return { grade, stage };
};

/**
 * Fetches preparatory stage HPC report data for the current student using background tasks
 * @returns {Promise<Object>} Preparatory stage HPC report data
 */
export const fetchPreparatoryReportData = async () => {
    try {
        const accessToken = localStorage.getItem("accessToken");
        if (!accessToken) {
            throw new Error("Authentication required. Please log in again.");
        }

        // Start background task for report generation with timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

        try {
            const generateResponse = await fetch(`${API_BASE_URL}/preparatory/user/student/hpc-report/generate`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!generateResponse.ok) {
                // If the generate endpoint fails, try the synchronous endpoint as fallback
                if (generateResponse.status === 504 || generateResponse.status === 502) {
                    console.warn("Preparatory stage background task endpoint timed out, falling back to synchronous endpoint");
                    return await fetchPreparatoryReportDataSync();
                }
                const errorBody = await generateResponse.text();
                throw new Error(`HTTP error ${generateResponse.status}: ${generateResponse.statusText}. Details: ${errorBody}`);
            }

            const { task_id } = await generateResponse.json();

            // Poll for task completion
            return await pollForPreparatoryTaskCompletion(task_id, accessToken);

        } catch (error) {
            clearTimeout(timeoutId);
            if (error.name === 'AbortError') {
                console.warn("Preparatory stage request timed out, falling back to synchronous endpoint");
                return await fetchPreparatoryReportDataSync();
            }
            throw error;
        }

    } catch (error) {
        console.error("Error fetching preparatory report:", error);
        // If all else fails, try the synchronous endpoint
        try {
            console.warn("Attempting preparatory stage synchronous fallback...");
            return await fetchPreparatoryReportDataSync();
        } catch (fallbackError) {
            console.error("Preparatory stage synchronous fallback also failed:", fallbackError);
            throw error; // Throw the original error
        }
    }
};

/**
 * Fallback function to fetch preparatory stage report data synchronously
 * @returns {Promise<Object>} Preparatory stage HPC report data
 */
const fetchPreparatoryReportDataSync = async () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
        throw new Error("Authentication required. Please log in again.");
    }

    const response = await fetch(`${API_BASE_URL}/preparatory/user/student/hpc-report`, {
        headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
        }
    });

    if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`HTTP error ${response.status}: ${response.statusText}. Details: ${errorBody}`);
    }

    return await response.json();
};

/**
 * Polls for preparatory stage task completion and returns the result
 * @param {string} taskId - The task ID to poll
 * @param {string} accessToken - The access token for authentication
 * @returns {Promise<Object>} The completed task result
 */
const pollForPreparatoryTaskCompletion = async (taskId, accessToken) => {
    const maxAttempts = 60; // 5 minutes with 5-second intervals
    const pollInterval = 5000; // 5 seconds

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
            // Check task status
            const statusResponse = await fetch(`${API_BASE_URL}/preparatory/tasks/${taskId}/status`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!statusResponse.ok) {
                throw new Error(`Failed to check task status: ${statusResponse.statusText}`);
            }

            const status = await statusResponse.json();

            if (status.status === 'completed') {
                // Get the result
                const resultResponse = await fetch(`${API_BASE_URL}/preparatory/tasks/${taskId}/result`, {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (!resultResponse.ok) {
                    throw new Error(`Failed to get task result: ${resultResponse.statusText}`);
                }

                return await resultResponse.json();
            } else if (status.status === 'failed') {
                throw new Error(`Report generation failed: ${status.error || 'Unknown error'}`);
            } else if (status.status === 'running' || status.status === 'pending') {
                // Log progress for debugging
                console.log(`Preparatory stage report generation progress: ${status.progress}% (${status.status})`);
                // Continue polling
                await new Promise(resolve => setTimeout(resolve, pollInterval));
                continue;
            }
        } catch (error) {
            console.warn(`Preparatory stage polling attempt ${attempt + 1}/${maxAttempts} failed:`, error.message);
            if (attempt === maxAttempts - 1) {
                throw new Error(`Preparatory stage report generation failed after ${maxAttempts} attempts: ${error.message}`);
            }
            // Wait before retrying
            await new Promise(resolve => setTimeout(resolve, pollInterval));
        }
    }

    throw new Error('Report generation timed out. Please try again.');
};

/**
 * Fetches student's profile picture with CORS handling
 * @returns {Promise<string|null>} Base64 encoded image URL or null
 */
export const fetchProfilePicture = async () => {
    try {
        const accessToken = localStorage.getItem("accessToken");
        if (!accessToken) {
            console.warn("No access token available for profile picture fetch");
            return null;
        }

        const server_url = API_BASE_URL;
        const response = await fetch(`${server_url}/users/me/profile-picture`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${accessToken}`,
                "Content-Type": "application/json"
            }
        });

        if (!response.ok) {
            if (response.status === 404) {
                return null; // No profile picture set
            }
            const errorData = await response.json();
            throw new Error(errorData.detail || "Failed to get profile picture");
        }

        const result = await response.json();

        if (result?.profile_picture_url) {
            // Create a promise to handle image loading properly
            const imageLoaded = new Promise((resolve, reject) => {
                const img = new Image();
                img.crossOrigin = "Anonymous"; // Set crossorigin attribute

                img.onload = () => {
                    try {
                        // Create canvas and draw image
                        const canvas = document.createElement('canvas');
                        canvas.width = img.width;
                        canvas.height = img.height;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(img, 0, 0);

                        // Convert to base64 - critical for PDF export
                        const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
                        resolve(dataUrl);
                    } catch (err) {
                        console.error("Error converting image to base64:", err);
                        resolve(getDefaultProfileImage());
                    }
                };

                img.onerror = (err) => {
                    console.error("Error loading student image:", err);
                    resolve(getDefaultProfileImage());
                };

                // Set source to trigger loading
                img.src = server_url + result.profile_picture_url;
            });

            // Wait for image to load and convert to base64
            return await imageLoaded;
        }
        return getDefaultProfileImage(); // Return placeholder if no profile picture
    } catch (error) {
        console.error("Error getting profile picture:", error);
        return getDefaultProfileImage(); // Return placeholder on error
    }
};

/**
 * Helper function to safely format a date string for display
 * @param {string} dateString - The date string to format
 * @returns {string} Formatted date string or error message
 */
export const displayDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            console.warn("Invalid date string provided for display:", dateString);
            return "Invalid Date";
        }
        return date.toLocaleDateString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    } catch (e) {
        console.error("Error formatting date for display:", dateString, e);
        return "Error Date";
    }
};

/**
 * Returns a default profile image as a data URL when the real image can't be loaded
 * @returns {string} Data URL of a simple placeholder image
 */
function getDefaultProfileImage() {
    // Create a placeholder image with the student's initials or a default avatar
    const canvas = document.createElement('canvas');
    canvas.width = 100;
    canvas.height = 100;
    const ctx = canvas.getContext('2d');

    // Background color
    ctx.fillStyle = '#f0f0f0';
    ctx.fillRect(0, 0, 100, 100);

    // Border
    ctx.strokeStyle = '#4B5563';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, 98, 98);

    // Text for placeholder
    ctx.fillStyle = '#6B7280';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Student', 50, 50);

    return canvas.toDataURL('image/png');
}


/**
 * Fetches the latest pre-generated (official) HPC report for the current student.
 * Returns null if no pre-generated report exists (404).
 * @returns {Promise<Object|null>} Pre-generated report object or null
 */
export const fetchPreGeneratedReport = async () => {
    try {
        const accessToken = localStorage.getItem("accessToken");
        if (!accessToken) {
            return null;
        }

        const response = await fetch(`${API_BASE_URL}/student/hpc-report/pre-generated`, {
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }
        });

        if (response.status === 404) {
            return null; // No pre-generated report — expected case
        }

        if (!response.ok) {
            console.warn("Failed to fetch pre-generated report:", response.status);
            return null;
        }

        return await response.json();
    } catch (error) {
        console.error("Error fetching pre-generated report:", error);
        return null;
    }
};
